import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Droplets, Recycle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1561041695-d2fadf9f318c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const Hero = ({ onScrollTo }) => {
  const { company } = useSettings();
  return (
    <section id="hero" className="relative min-h-[100svh] flex items-center overflow-hidden bg-gradient-to-br from-sky-700 via-sky-600 to-blue-800">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Cool Waves bottled water splash" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-sky-950/90 via-sky-900/70 to-cyan-900/60" />
      </div>
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-cyan-400 rounded-full cw-blob" />
      <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] bg-sky-500 rounded-full cw-blob" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-20 grid lg:grid-cols-12 gap-10 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full cw-glass-dark border border-sky-400/30 text-sky-100 text-xs font-semibold uppercase tracking-[0.2em] mb-6"
          >
            <Droplets className="w-3.5 h-3.5 text-sky-300" /> {company?.service_area || "Kingston · St. Andrew · Jamaica"}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]"
          >
            Pure Hydration. <br />
            <span className="cw-text-gradient">Refreshing.Clean.Reliable</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-sky-100/85 max-w-xl leading-relaxed"
          >
            Cool Waves Water is carefully purified for a crisp, clean taste in every size. From everyday bottles to
            dependable 5&nbsp;gallon supply, we make refreshing hydration easy for Jamaican homes and businesses.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Button
              data-testid="hero-cta-order-now"
              onClick={() => onScrollTo("order")}
              size="lg"
              className="rounded-full h-14 px-8 text-base font-bold bg-sky-400 hover:bg-sky-300 text-sky-950 shadow-xl shadow-sky-500/30"
            >
              Order Now <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <Button
              data-testid="hero-cta-refill-plans"
              onClick={() => onScrollTo("refills")}
              size="lg"
              variant="outline"
              className="rounded-full h-14 px-8 text-base font-bold bg-white/5 border-sky-300/40 text-white hover:bg-white/15 hover:text-white"
            >
              Explore Refill Plans
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 flex flex-wrap gap-6 text-sky-100/80"
          >
            {[
              { icon: ShieldCheck, label: "Multi-stage UV + RO" },
              { icon: Recycle, label: "Eco bottle swap" },
              { icon: Droplets, label: "Doorstep delivery" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm font-semibold">
                <f.icon className="w-4 h-4 text-sky-300" /> {f.label}
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 hidden lg:flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative cw-float"
          >
            <div className="w-72 h-72 rounded-[2.5rem] overflow-hidden border border-sky-300/20 shadow-2xl relative">
              <img src="/brand/bottle-crop.png" alt="Cool Waves Water 1.5L bottle" className="w-full h-full object-cover" data-testid="hero-bottle-img" />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-sky-950/90 to-transparent text-center">
                <div className="font-display text-4xl font-extrabold text-white">100%</div>
                <div className="text-sky-200 text-xs font-semibold uppercase tracking-widest">Purified</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
